import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, manhwaList } from '@/mocks/manhwa';
import { faqs } from '@/mocks/manhwa';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const filteredManhwa = useMemo(() => {
    const list = manhwaList.filter((m) => m.category === slug);
    if (sortBy === 'latest') {
      return [...list].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    }
    if (sortBy === 'popular') {
      return [...list].sort((a, b) => b.views - a.views);
    }
    return [...list].sort((a, b) => b.rating - a.rating);
  }, [slug, sortBy]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">分类不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm whitespace-nowrap cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const sortOptions = [
    { value: 'latest' as const, label: '最新更新' },
    { value: 'popular' as const, label: '最受欢迎' },
    { value: 'rating' as const, label: '评分最高' },
  ];

  return (
    <>
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="CollectionPage" data={{
        name: `韩漫漫画网 - ${category.name}韩国漫画`,
        description: `${category.name}韩国漫画免费在线阅读，${category.description}`,
        url: `https://bluefocugroup.cn/category/${slug}`,
      }} />

      <Breadcrumb items={[{ label: category.name, href: `/category/${slug}` }]} />

      <main className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
        {/* Category Header */}
        <section className="py-8 md:py-12">
          <div className="flex items-center gap-4 mb-4">
            <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-50 text-primary-500">
              <i className={`${category.icon} text-2xl`}></i>
            </span>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">{category.name}</h1>
              <p className="text-sm text-foreground-500">{category.description}</p>
            </div>
          </div>
        </section>

        {/* Sort Controls */}
        <div className="flex items-center gap-2 mb-8 bg-background-100 rounded-xl p-1 w-fit">
          {sortOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSortBy(opt.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                sortBy === opt.value
                  ? 'bg-background-50 text-foreground-950'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Manhwa Grid */}
        {filteredManhwa.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
            {filteredManhwa.map((manhwa) => (
              <Link
                key={manhwa.id}
                to={`/article/${manhwa.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer"
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={manhwa.cover}
                    alt={manhwa.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1 line-clamp-1">{manhwa.title}</h3>
                  <p className="text-xs text-foreground-500 line-clamp-1 mb-2">{manhwa.description}</p>
                  <div className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                      {manhwa.rating}
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="bg-primary-50 text-primary-600 px-2 py-0.5 rounded-full text-xs whitespace-nowrap">{manhwa.status}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <p className="text-foreground-500 text-sm">该分类下暂无漫画，敬请期待</p>
          </div>
        )}
      </main>
    </>
  );
}